import { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Banknote, CreditCard, RefreshCcw, ShieldAlert, Wrench } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { DataErrorBanner, type DataErrorDetail } from '@/components/ui/data-error-banner';
import { api, type PaginatedData } from '@/lib/api';
import {
  formatMoney,
  type DepotBillableCharge,
  type DepotBillingRun,
  type DepotRepairEstimate,
  useDepotList,
} from './depot-hooks';
import {
  DepotEmptyRow,
  DepotGridPanel,
  depotTableClassName,
  depotTableHeadClassName,
  depotTdClassName,
  depotThClassName,
} from './depot-grid';

type DepotRateCard = {
  id: string;
  data?: {
    module?: string;
    billToName?: string;
    chargeType?: string;
  } | null;
};

function normalizeKey(value?: string | null): string {
  return value?.trim() || 'Unassigned';
}

function toAmount(value: string | number | null | undefined): number {
  const parsed = typeof value === 'string' ? Number(value) : value ?? 0;
  return Number.isFinite(parsed) ? Number(parsed) : 0;
}

export default function DepotRevenuePage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const billable = useDepotList<DepotBillableCharge>('/depot/charges/billable?limit=100');
  const handoffs = useDepotList<DepotBillableCharge>('/depot/charges/handoffs?limit=100');
  const runs = useDepotList<DepotBillingRun>('/depot/billing-runs?limit=24');
  const repairs = useDepotList<DepotRepairEstimate>('/depot/repair-estimates?limit=100');
  const [rateCards, setRateCards] = useState<DepotRateCard[]>([]);
  const [rateCardError, setRateCardError] = useState<string | null>(null);
  const [rateCardLoading, setRateCardLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const accountFilter = searchParams.get('account')?.trim() ?? '';
  const filterKey = normalizeKey(accountFilter);

  const error = billable.error ?? handoffs.error ?? runs.error ?? repairs.error ?? rateCardError;
  const loading = billable.loading || handoffs.loading || runs.loading || repairs.loading || rateCardLoading;

  const pageError: DataErrorDetail | null = error
    ? {
        endpoint: '/depot/charges/billable + /depot/charges/handoffs + /depot/billing-runs + /depot/repair-estimates + /rate-cards',
        message: error,
        status: 500,
        code: 'DEPOT_REVENUE_LOAD_FAILED',
      }
    : null;

  const loadRateCards = async () => {
    setRateCardLoading(true);
    setRateCardError(null);
    try {
      const response = await api.get<PaginatedData<DepotRateCard>>('/rate-cards?limit=100');
      setRateCards(response.items.filter((item) => item.data?.module === 'depot'));
    } catch (err) {
      setRateCardError(err instanceof Error ? err.message : 'Failed to load depot tariffs');
    } finally {
      setRateCardLoading(false);
    }
  };

  useEffect(() => {
    void loadRateCards();
  }, []);

  const handleRefresh = async () => {
    setRefreshing(true);
    await Promise.all([billable.refresh(), handoffs.refresh(), runs.refresh(), repairs.refresh(), loadRateCards()]);
    setRefreshing(false);
  };

  const visibleBillable = useMemo(
    () => (accountFilter ? billable.items.filter((item) => normalizeKey(item.bill_to_name ?? item.line_operator) === filterKey) : billable.items),
    [accountFilter, billable.items, filterKey],
  );

  const visibleHandoffs = useMemo(
    () => (accountFilter ? handoffs.items.filter((item) => normalizeKey(item.bill_to_name ?? item.line_operator) === filterKey) : handoffs.items),
    [accountFilter, filterKey, handoffs.items],
  );

  const operatorSummary = useMemo(() => {
    const map = new Map<string, {
      name: string;
      readyAmount: number;
      handoffAmount: number;
      disputedAmount: number;
      chargeCount: number;
    }>();

    visibleBillable.forEach((item) => {
      const key = normalizeKey(item.bill_to_name ?? item.line_operator);
      const current = map.get(key) ?? { name: key, readyAmount: 0, handoffAmount: 0, disputedAmount: 0, chargeCount: 0 };
      current.readyAmount += toAmount(item.amount);
      current.chargeCount += 1;
      if (item.charge_status === 'disputed') current.disputedAmount += toAmount(item.amount);
      map.set(key, current);
    });

    visibleHandoffs.forEach((item) => {
      const key = normalizeKey(item.bill_to_name ?? item.line_operator);
      const current = map.get(key) ?? { name: key, readyAmount: 0, handoffAmount: 0, disputedAmount: 0, chargeCount: 0 };
      current.handoffAmount += toAmount(item.amount);
      map.set(key, current);
    });

    return Array.from(map.values()).sort((a, b) => (b.readyAmount + b.handoffAmount) - (a.readyAmount + a.handoffAmount));
  }, [visibleBillable, visibleHandoffs]);

  const chargeMix = useMemo(() => {
    const map = new Map<string, { type: string; amount: number; count: number }>();
    [...visibleBillable, ...visibleHandoffs].forEach((item) => {
      const key = item.charge_type || 'other';
      const current = map.get(key) ?? { type: key, amount: 0, count: 0 };
      current.amount += toAmount(item.amount);
      current.count += 1;
      map.set(key, current);
    });
    return Array.from(map.values()).sort((a, b) => b.amount - a.amount);
  }, [visibleBillable, visibleHandoffs]);

  const kpis = useMemo(() => {
    const readyAmount = visibleBillable.reduce((sum, item) => sum + toAmount(item.amount), 0);
    const handedOffAmount = visibleHandoffs.reduce((sum, item) => sum + toAmount(item.amount), 0);
    const disputedAmount = visibleBillable
      .filter((item) => item.charge_status === 'disputed')
      .reduce((sum, item) => sum + toAmount(item.amount), 0);
    const repairPipeline = repairs.items.reduce((sum, item) => sum + toAmount(item.total_amount), 0);
    return { readyAmount, handedOffAmount, disputedAmount, repairPipeline };
  }, [repairs.items, visibleBillable, visibleHandoffs]);

  const tariffCoverage = useMemo(() => {
    const coveredAccounts = new Set(rateCards.map((item) => normalizeKey(item.data?.billToName)));
    const rows = operatorSummary.map((item) => ({
      ...item,
      hasTariff: coveredAccounts.has(item.name),
    }));
    const coveredAmount = rows.filter((item) => item.hasTariff).reduce((sum, item) => sum + item.readyAmount + item.handoffAmount, 0);
    const uncoveredAmount = rows.filter((item) => !item.hasTariff).reduce((sum, item) => sum + item.readyAmount + item.handoffAmount, 0);
    return { rows, coveredAmount, uncoveredAmount, templateCount: rateCards.length };
  }, [operatorSummary, rateCards]);

  return (
    <div className="space-y-5">
      {pageError && <DataErrorBanner error={pageError} entityLabel="Depot Revenue" onRetry={handleRefresh} />}

      <div className="flex items-center justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold">Depot Revenue</h2>
          <p className="text-sm text-muted-foreground">
            Monetization control for gate, handling, storage, inspection, repair, and wash services.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" asChild>
            <Link to="/depot/accounts">Accounts View</Link>
          </Button>
          <Button variant="outline" size="sm" asChild>
            <Link to="/depot/billing">Billing Desk</Link>
          </Button>
          <Button variant="outline" size="sm" onClick={handleRefresh} disabled={refreshing || loading}>
            <RefreshCcw className={`mr-1.5 h-3.5 w-3.5 ${refreshing ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          {accountFilter && (
            <Button variant="ghost" size="sm" onClick={() => setSearchParams({}, { replace: true })}>
              Clear Filter
            </Button>
          )}
        </div>
      </div>

      {accountFilter && (
        <div className="text-xs text-muted-foreground">
          Filtered account: <span className="font-medium text-foreground">{accountFilter}</span>
        </div>
      )}

      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
        <div className="rounded-lg border bg-background px-3 py-3">
          <div className="mb-1 flex items-center justify-between text-xs text-muted-foreground">
            <span>Ready to Bill</span>
            <CreditCard className="h-4 w-4 text-sky-700" />
          </div>
          <div className="text-2xl font-bold">{loading ? '—' : formatMoney(kpis.readyAmount)}</div>
        </div>
        <div className="rounded-lg border bg-background px-3 py-3">
          <div className="mb-1 flex items-center justify-between text-xs text-muted-foreground">
            <span>Handed Off</span>
            <Banknote className="h-4 w-4 text-emerald-700" />
          </div>
          <div className="text-2xl font-bold">{loading ? '—' : formatMoney(kpis.handedOffAmount)}</div>
        </div>
        <div className="rounded-lg border bg-background px-3 py-3">
          <div className="mb-1 flex items-center justify-between text-xs text-muted-foreground">
            <span>Disputed</span>
            <ShieldAlert className="h-4 w-4 text-rose-700" />
          </div>
          <div className="text-2xl font-bold">{loading ? '—' : formatMoney(kpis.disputedAmount)}</div>
        </div>
        <div className="rounded-lg border bg-background px-3 py-3">
          <div className="mb-1 flex items-center justify-between text-xs text-muted-foreground">
            <span>Repair Pipeline</span>
            <Wrench className="h-4 w-4 text-violet-700" />
          </div>
          <div className="text-2xl font-bold">{loading ? '—' : formatMoney(kpis.repairPipeline)}</div>
        </div>
        <div className="rounded-lg border bg-background px-3 py-3">
          <div className="mb-1 flex items-center justify-between text-xs text-muted-foreground">
            <span>Tariff Templates</span>
            <CreditCard className="h-4 w-4 text-slate-700" />
          </div>
          <div className="text-2xl font-bold">{loading ? '—' : tariffCoverage.templateCount}</div>
        </div>
      </div>

      <DepotGridPanel title="Revenue by Account" subtitle="Account-level revenue signal, ready charges, handoff amount, and dispute amount.">
        <table className={depotTableClassName}>
          <thead className={depotTableHeadClassName}>
            <tr>
              <th className={depotThClassName}>Account</th>
              <th className={depotThClassName}>Charges</th>
              <th className={depotThClassName}>Ready</th>
              <th className={depotThClassName}>Handoff</th>
              <th className={depotThClassName}>Disputed</th>
              <th className={depotThClassName}>Total Signal</th>
              <th className={depotThClassName}>Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y">
            {operatorSummary.length === 0 ? (
              <DepotEmptyRow colSpan={7}>No depot revenue rows yet.</DepotEmptyRow>
            ) : (
              operatorSummary.map((item) => (
                <tr key={item.name} className="hover:bg-muted/30">
                  <td className={`${depotTdClassName} font-medium`}>{item.name}</td>
                  <td className={depotTdClassName}>{item.chargeCount}</td>
                  <td className={depotTdClassName}>{formatMoney(item.readyAmount)}</td>
                  <td className={depotTdClassName}>{formatMoney(item.handoffAmount)}</td>
                  <td className={depotTdClassName}>{formatMoney(item.disputedAmount)}</td>
                  <td className={depotTdClassName}>
                    <Badge variant={item.disputedAmount > 0 ? 'destructive' : 'secondary'}>
                      {formatMoney(item.readyAmount + item.handoffAmount)}
                    </Badge>
                  </td>
                  <td className={depotTdClassName}>
                    <div className="flex flex-wrap gap-2">
                      <Button size="sm" variant="ghost" asChild>
                        <Link to={`/depot/profitability?account=${encodeURIComponent(item.name)}`}>Profitability</Link>
                      </Button>
                      <Button size="sm" variant="ghost" asChild>
                        <Link to={`/depot/tariff?account=${encodeURIComponent(item.name)}`}>Tariff</Link>
                      </Button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </DepotGridPanel>

      <DepotGridPanel title="Charge Mix" subtitle="Activity-based revenue by charge type.">
        <table className={depotTableClassName}>
          <thead className={depotTableHeadClassName}>
            <tr>
              <th className={depotThClassName}>Charge Type</th>
              <th className={depotThClassName}>Count</th>
              <th className={depotThClassName}>Amount</th>
            </tr>
          </thead>
          <tbody className="divide-y">
            {chargeMix.length === 0 ? (
              <DepotEmptyRow colSpan={3}>No depot charge mix yet.</DepotEmptyRow>
            ) : (
              chargeMix.map((item) => (
                <tr key={item.type} className="hover:bg-muted/30">
                  <td className={depotTdClassName}>{item.type}</td>
                  <td className={depotTdClassName}>{item.count}</td>
                  <td className={depotTdClassName}>{formatMoney(item.amount)}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </DepotGridPanel>

      <DepotGridPanel
        title="Tariff Coverage by Account"
        subtitle={`Covered ${formatMoney(tariffCoverage.coveredAmount)} · Uncovered ${formatMoney(tariffCoverage.uncoveredAmount)}`}
        action={<Button variant="ghost" size="sm" asChild><Link to="/depot/tariff">Open Tariff</Link></Button>}
      >
        <table className={depotTableClassName}>
          <thead className={depotTableHeadClassName}>
            <tr>
              <th className={depotThClassName}>Account</th>
              <th className={depotThClassName}>Coverage</th>
              <th className={depotThClassName}>Total Signal</th>
              <th className={depotThClassName}>Ready</th>
              <th className={depotThClassName}>Handoff</th>
              <th className={depotThClassName}>Disputed</th>
            </tr>
          </thead>
          <tbody className="divide-y">
            {tariffCoverage.rows.length === 0 ? (
              <DepotEmptyRow colSpan={6}>No depot account activity yet.</DepotEmptyRow>
            ) : (
              tariffCoverage.rows.map((item) => (
                <tr key={item.name} className="hover:bg-muted/30">
                  <td className={`${depotTdClassName} font-medium`}>{item.name}</td>
                  <td className={depotTdClassName}>
                    <Badge variant={item.hasTariff ? 'secondary' : 'outline'}>{item.hasTariff ? 'tariff covered' : 'no tariff'}</Badge>
                  </td>
                  <td className={depotTdClassName}>{formatMoney(item.readyAmount + item.handoffAmount)}</td>
                  <td className={depotTdClassName}>{formatMoney(item.readyAmount)}</td>
                  <td className={depotTdClassName}>{formatMoney(item.handoffAmount)}</td>
                  <td className={depotTdClassName}>{formatMoney(item.disputedAmount)}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </DepotGridPanel>

      <DepotGridPanel title="Recent Handoffs" subtitle="Finance handoff rows for recent depot charges.">
        <table className={depotTableClassName}>
          <thead className={depotTableHeadClassName}>
            <tr>
              <th className={depotThClassName}>Charge</th>
              <th className={depotThClassName}>Account</th>
              <th className={depotThClassName}>Type</th>
              <th className={depotThClassName}>Amount</th>
              <th className={depotThClassName}>Status</th>
            </tr>
          </thead>
          <tbody className="divide-y">
            {handoffs.items.length === 0 ? (
              <DepotEmptyRow colSpan={5}>No finance handoffs yet.</DepotEmptyRow>
            ) : (
              handoffs.items.slice(0, 8).map((item) => (
                <tr key={item.id} className="hover:bg-muted/30">
                  <td className={`${depotTdClassName} font-mono font-semibold`}>{item.charge_no}</td>
                  <td className={depotTdClassName}>{item.bill_to_name ?? item.line_operator ?? 'Unassigned'}</td>
                  <td className={depotTdClassName}>{item.charge_type}</td>
                  <td className={depotTdClassName}>{formatMoney(item.amount, item.currency)}</td>
                  <td className={depotTdClassName}><Badge variant={item.billing_entry_status === 'posted' ? 'secondary' : 'outline'}>{item.billing_entry_status ?? 'draft'}</Badge></td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </DepotGridPanel>

      <DepotGridPanel
        title="Billing Runs"
        subtitle="Batch billing history."
        action={<Button variant="ghost" size="sm" asChild><Link to="/depot/billing">Open Billing</Link></Button>}
      >
        <table className={depotTableClassName}>
          <thead className={depotTableHeadClassName}>
            <tr>
              <th className={depotThClassName}>Run</th>
              <th className={depotThClassName}>Mode</th>
              <th className={depotThClassName}>Items</th>
              <th className={depotThClassName}>Groups</th>
              <th className={depotThClassName}>Amount</th>
              <th className={depotThClassName}>Status</th>
            </tr>
          </thead>
          <tbody className="divide-y">
            {runs.items.length === 0 ? (
              <DepotEmptyRow colSpan={6}>No billing runs yet.</DepotEmptyRow>
            ) : (
              runs.items.slice(0, 8).map((item) => (
                <tr key={item.id} className="hover:bg-muted/30">
                  <td className={`${depotTdClassName} font-mono font-semibold`}>{item.run_ref}</td>
                  <td className={depotTdClassName}>{item.run_mode}</td>
                  <td className={depotTdClassName}>{item.item_count}</td>
                  <td className={depotTdClassName}>{item.group_count}</td>
                  <td className={depotTdClassName}>{formatMoney(item.total_amount)}</td>
                  <td className={depotTdClassName}><Badge variant={item.status === 'completed' ? 'secondary' : 'outline'}>{item.status}</Badge></td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </DepotGridPanel>
    </div>
  );
}
